import { ConnectedPosition } from '@angular/cdk/overlay';
import { Component, EventEmitter, Inject, inject, Input, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { PopoverPositions } from '@vendasta/galaxy/popover';
import { Observable, combineLatest, of } from 'rxjs';
import { switchMap, map, shareReplay, catchError, filter } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';
import { getCallToActionUrl, popUpShouldRedirectToCTA } from './pop-up-cta-check';
import { GalaxyNavItemListComponent } from '@vendasta/galaxy/nav';
import { TranslateService } from '@ngx-translate/core';
import { Salesperson, SalespersonService } from '@vendasta/sales';

export interface MenuItem {
  ngRoute?: boolean;
  url?: string;
  label: string;
  menuId: string;
  i18n_key?: string;
  icon?: string;
  nestedItems?: MenuItem[];
  expanded?: boolean;
  rightIcon?: string;
  popOut?: boolean;
  svgIcon?: boolean;
  locked?: boolean;
  subsectionHeader?: string;
  badge?: string;
}

enum NavLinkType {
  NAVLINK = 0,
  SUBNAV = 1,
  DISABLED = 2,
  LOCKED = 3,
  POPOUT = 4,
  BETA = 5,
  NEW = 6,
}

@Component({
  selector: 'app-nav-item',
  templateUrl: './app-nav-item.component.html',
  styleUrls: ['./app-nav-item.component.scss'],
  standalone: false,
})
export class AppNavItemComponent implements OnInit {
  @Input() activeMenuId: string;
  @Input() item: MenuItem;
  @Output() clicked: EventEmitter<GalaxyNavItemListComponent> = new EventEmitter<GalaxyNavItemListComponent>();

  private readonly router = inject(Router);
  private readonly configService = inject(AppConfigService);
  private readonly translateService = inject(TranslateService);
  private readonly salespersonService = inject(SalespersonService);

  public shouldExpandMenu = false;
  clickWillRedirectToCTA = false;

  readonly navLinkTypes = NavLinkType;
  navType: NavLinkType = NavLinkType.NAVLINK;
  navTitle: string;
  linkIsHandledInComponent: boolean;

  readonly positions: ConnectedPosition[] = [{ ...PopoverPositions.Right }, { ...PopoverPositions.Bottom }];
  tooltip = `NAV_ITEMS.DEFAULT_DISABLED_TOOLTIP`;

  // Track if current user is a salesperson
  private isSalesperson = signal<boolean>(false);
  private isSalesperson$: Observable<boolean>;

  constructor(@Inject('PARTNER_ID') readonly partnerId$: Observable<string>) {
    // Initialize salesperson status observable
    this.isSalesperson$ = this.getSalespersonStatus();

    // Subscribe to salesperson status and update navigation state
    this.isSalesperson$.pipe(takeUntilDestroyed()).subscribe((isSalesperson) => {
      this.isSalesperson.set(isSalesperson);
      this.linkIsHandledInComponent =
        this.navType === NavLinkType.DISABLED || this.navType === NavLinkType.LOCKED || this.clickWillRedirectToCTA;
      this.navType = this.getNavType();
      this.navTitle = this.getNavTitle();
    });
  }

  ngOnInit(): void {
    if (this.item.nestedItems) {
      const activeItems = this.item.nestedItems.filter((item) => this.activeMenuId.startsWith(item.menuId));
      this.shouldExpandMenu = activeItems.length > 0;
    }
    this.clickWillRedirectToCTA = popUpShouldRedirectToCTA(this.item);
    this.linkIsHandledInComponent =
      this.navType === NavLinkType.DISABLED || this.navType === NavLinkType.LOCKED || this.clickWillRedirectToCTA;
    this.navType = this.getNavType();
    this.navTitle = this.getNavTitle();
  }

  private getSalespersonStatus(): Observable<boolean> {
    const uid$ = this.configService.config$.pipe(
      map((config) => config.unifiedUserId),
      filter((userId): userId is string => !!userId), // Filter out null/undefined values
    );

    const partnerId$ = this.partnerId$.pipe(
      filter((partnerId): partnerId is string => !!partnerId), // Filter out null/undefined values
    );

    return combineLatest([partnerId$, uid$]).pipe(
      switchMap(([partnerId, userId]) => this.salespersonService.getSalespersonByUserId(partnerId, userId)),
      map((salesperson: Salesperson) => !!salesperson && !!salesperson.salespersonId),
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError(() => of(false)),
    );
  }

  onClick($event: GalaxyNavItemListComponent): void {
    // Double check for disabled state
    if (this.navType === NavLinkType.DISABLED) {
      return;
    }

    this.clicked.emit($event);
  }

  navTabClicked(item: MenuItem): void {
    // Double check for disabled state
    if (this.navType === NavLinkType.DISABLED) {
      return;
    }
    if (!item.popOut) {
      this.router.navigate([item.url]);
    }

    if (this.clickWillRedirectToCTA) {
      this.handleLockedPopUpClick(item);
    }
  }

  private handleLockedPopUpClick(item: MenuItem): void {
    const ctaUrl = getCallToActionUrl(item);
    this.router.navigate([ctaUrl], { queryParams: { page: item.menuId } });
  }

  private getNavType(): NavLinkType {
    if (this.item.menuId === 'open-ssc') {
      if (this.configService.config.isSuperAdmin) {
        this.tooltip = `NAV_ITEMS.SALES.SALES_POPOUTS_DISABLED_TOOLTIP`;
        return NavLinkType.DISABLED;
      }
    }

    if (this.item.menuId === 'open-meetings') {
      if (this.configService.config.isSuperAdmin) {
        if (this.isSalesperson()) {
          this.tooltip = '';
          return NavLinkType.NAVLINK;
        } else {
          this.tooltip = `NAV_ITEMS.SUPER_ADMIN_DISABLED_TOOLTIP`;
          return NavLinkType.DISABLED;
        }
      } else if (this.item.locked) {
        this.tooltip = `NAV_ITEMS.SALES.MY_MEETINGS_POPOUTS_DISABLED_TOOLTIP`;
        return NavLinkType.DISABLED;
      }
    }

    if (this.item.locked) {
      return NavLinkType.LOCKED;
    }

    if (this.item.popOut) {
      return NavLinkType.POPOUT;
    }

    if (this.item.rightIcon) {
      return NavLinkType.SUBNAV;
    }

    if (this.item.badge === 'NAVIGATION_ITEM_BADGE_NEW') {
      return NavLinkType.NEW;
    }

    if (this.item.badge === 'NAVIGATION_ITEM_BADGE_BETA') {
      return NavLinkType.BETA;
    }

    return NavLinkType.NAVLINK;
  }

  getNavTitle(): string {
    if (this.item.i18n_key) {
      return this.translateService.instant(this.item.i18n_key);
    }
    return this.item.label;
  }
}
